import React, { useEffect, useMemo } from 'react';
import { View } from 'react-native';
import Input from '@/components/General/inputs/Input';
import SelectInput from '@/components/General/inputs/SelectInput';
import InputSection from '@/components/General/inputs/InputSection';
import Callout from '@/components/General/Callout';
import states from '@/utils/constants/states'
import { saveAddress } from '@/data/people';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import {useEditScreenStore} from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

const EMPTY_DRAFT = {};

export default function AddAddressModal() {

    const { id, fromScreen, fromStoreKey, fromPersonID } = useLocalSearchParams();
    const storeKey = id || "new";
    const isEdit = !!id;
    
    const router = useRouter()
    const queryClient = useQueryClient();

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData);
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData);
    const addressDetails = useEditScreenStore(s => s.AddAddressModal?.[storeKey]) || EMPTY_DRAFT

    const {data: addressToEdit} = Query.addresses.byId({id, options: {enabled: !!id, placeholderData: {}}})

    const otherPeopleCount = useMemo(() => {
        const members = addressToEdit?.HouseholdMembers || [];
        const filtered = fromPersonID ? members.filter(m => m.id !== fromPersonID) : members;
        return filtered.length;
    }, [addressToEdit?.HouseholdMembers, fromPersonID]);

    const saveAddressMutation = useMutation({
        mutationFn: saveAddress,
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.addresses.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.people.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.locations.all()});

            removeEditScreenData("AddAddressModal", storeKey)

            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else{
                redirectToDefault(router, "AddAddressModal")
            }
        },
        onError: (err) => {
            console.debug(err)
        },
    })

    useEffect(() => {
        if(addressToEdit){
            setAddressDetails({
                ...addressToEdit,
                originalValues: addressToEdit,
            })
        }
    }, [addressToEdit])

    const setAddressDetails = (new_details) => {
        setEditScreenData("AddAddressModal", storeKey, new_details)
    }

    return (
        <ModalWrapper
            onConfirm={() => {
                const { originalValues, ...cleanDetails } = addressDetails;
                saveAddressMutation.mutate({details: cleanDetails, originalValues})
            }} 
            title={isEdit ? "Edit Address" : "New Address"}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" contentContainerStyle={{alignItems: "center"}}>
                <View style={{paddingBottom:20, width:"100%"}}>
                    {(isEdit && otherPeopleCount > 0) && (
                        <View style={{paddingBottom: 12}}>
                            <Callout
                                type="warning"
                                message={`${otherPeopleCount} other ${otherPeopleCount === 1 ? 'person' : 'people'} share this address and will also be updated.`}
                            />
                        </View>
                    )}
                    <InputSection description={isEdit ? "Changes will apply to all people and locations using this address." : "This address will be able to be attached to people or locations in the future."}>
                        <Input
                            title="Street"
                            autoCapitalize="words"
                            autoComplete='street-address'
                            value={addressDetails.street}
                            onUpdate={(new_value) => {
                                setAddressDetails({street: new_value })
                            }}
                        />

                        <Input
                            title="Street Line 2"
                            autoCapitalize="words"
                            value={addressDetails.streetLine2}
                            onUpdate={(new_value) => {
                                setAddressDetails({streetLine2: new_value })
                            }}
                        />

                        <Input
                            title="City"
                            autoCapitalize="words"
                            value={addressDetails.city}
                            onUpdate={(new_value) => {
                                setAddressDetails({city: new_value })
                            }}
                        />

                        <SelectInput
                            placeholder="Select a State"
                            options={states}
                            title="State"
                            value={addressDetails.state}
                            onUpdate={(new_value) => {
                                setAddressDetails({state: new_value })
                            }}
                        />

                        <Input
                            title="Zip"
                            type="number-pad"
                            autoComplete='postal-code'
                            value={addressDetails.zip}
                            onUpdate={(new_value) => {
                                new_value = new_value.slice(0, 5);

                                setAddressDetails({zip: new_value })
                            }}
                        />
                    </InputSection>
                </View>
            </KeyboardAwareScrollView>
        </ModalWrapper> 
    );
}